import { useEffect, useRef, useState } from "react";
import {
  SALES_DISPLAY_PREVIEW_HEIGHT,
  SALES_DISPLAY_PREVIEW_WIDTH,
} from "@/lib/sales-display-preview";

interface Props {
  /** Ruta som lastes i iframen, relativ til appen. */
  src: string;
  title: string;
  /** Kalles med iframens vindu når den er lastet, så foreldren kan sende meldinger. */
  onReady?: (frame: HTMLIFrameElement) => void;
}

/**
 * VIB-2705: en TV-flate krympet ned til å passe i et kort.
 *
 * Iframen får alltid full TV-størrelse og skaleres med `transform`. Den kan
 * ikke bare settes til bredden til kortet: da ville siden inni tegnet seg som
 * om skjermen VAR så liten, og vi hadde sett et annet oppsett enn TV-en viser.
 * Skalering gir et ekte miniatyrbilde av den store flata.
 */
export function ScaledDisplayFrame({ src, title, onReady }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setScale(width / SALES_DISPLAY_PREVIEW_WIDTH);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg border bg-[#081F3B]"
      style={{ aspectRatio: `${SALES_DISPLAY_PREVIEW_WIDTH} / ${SALES_DISPLAY_PREVIEW_HEIGHT}` }}
    >
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        // Ikke interaktiv: previewen skal ses på, og et klikk inni ville truffet
        // «Vis neste slide»-flata på displayet og flyttet loopen bak ryggen på
        // knappene her ute.
        tabIndex={-1}
        onLoad={() => frameRef.current && onReady?.(frameRef.current)}
        style={{
          width: SALES_DISPLAY_PREVIEW_WIDTH,
          height: SALES_DISPLAY_PREVIEW_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          // Skjul flimmeret før første måling: uten dette vises iframen ett
          // bilde i full størrelse og spretter deretter ned på plass.
          visibility: scale > 0 ? "visible" : "hidden",
        }}
        className="pointer-events-none absolute left-0 top-0 border-0"
      />
    </div>
  );
}
